import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { submitEarlyAccess } from "@/lib/api";

export default function EarlyAccess() {
  const { t, isRTL } = useI18n();
  const f = t("early.form");

  const [form, setForm] = useState({
    full_name: "", email: "", mobile: "", emirate: "",
    device_type: "", primary_use_case: "", frequent_venue: "",
    beta_interest: true, consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((prev) => ({
    ...prev,
    [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.mobile || !form.emirate || !form.device_type) {
      toast.error(t("common.required"));
      return;
    }
    if (!form.consent) {
      toast.error(f.consent, { id: "early-consent-error" });
      return;
    }
    setSubmitting(true);
    try {
      await submitEarlyAccess(form);
      toast.success(f.success, { id: "early-access-success" });
      setDone(true);
      setForm({ full_name: "", email: "", mobile: "", emirate: "", device_type: "", primary_use_case: "", frequent_venue: "", beta_interest: true, consent: false });
    } catch {
      toast.error(t("business.form.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="early-access" className="section bg-white relative overflow-hidden" data-testid="section-early">
      <div className="absolute -z-10 -top-24 -end-24 h-[420px] w-[420px] rounded-full bg-brand-greenSoft blur-3xl opacity-70" />

      <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
        <div>
          <div className="eyebrow">{t("early.eyebrow")}</div>
          <h2 className="h2 mt-4">{t("early.title")}</h2>
          <p className="lede mt-5">{t("early.lede")}</p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "iPhone & Android beta",
              "Arabic & English",
              "Priority venue support",
              "Founding member perks",
            ].map((k) => (
              <div key={k} className="flex items-center gap-2 rounded-xl border border-brand-line bg-brand-sand px-4 py-3 text-sm font-semibold text-brand-charcoal">
                <CheckCircle2 className="h-4 w-4 text-brand-green" /> {k}
              </div>
            ))}
          </div>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-3xl border border-brand-green/40 bg-brand-greenSoft p-10 text-center"
            data-testid="early-success"
          >
            <div className="mx-auto h-14 w-14 rounded-full bg-brand-green text-white flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <div className="mt-5 text-xl font-bold text-brand-charcoal">{f.success}</div>
            <button
              onClick={() => setDone(false)}
              className="mt-6 btn-outline"
              data-testid="early-reset"
            >
              {t("nav.cta_early")}
            </button>
          </motion.div>
        ) : (
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl border border-brand-line bg-white p-8 md:p-10 shadow-soft"
            data-testid="early-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={f.full_name} value={form.full_name} onChange={update("full_name")} testid="early-full-name" required />
              <Field label={f.email} type="email" value={form.email} onChange={update("email")} testid="early-email" required />
              <Field label={f.mobile} value={form.mobile} onChange={update("mobile")} testid="early-mobile" required />
              <Select label={f.emirate} value={form.emirate} onChange={update("emirate")} placeholder={f.emirate_ph} options={f.emirates} testid="early-emirate" required />
              <Select label={f.device} value={form.device_type} onChange={update("device_type")} placeholder="—" options={f.devices} testid="early-device" required />
              <Field label={f.use_case} value={form.primary_use_case} onChange={update("primary_use_case")} placeholder={f.use_case_ph} testid="early-use-case" />
              <div className="md:col-span-2">
                <Field label={f.venue} value={form.frequent_venue} onChange={update("frequent_venue")} testid="early-venue" />
              </div>
              <label className="md:col-span-2 flex items-center gap-3 text-sm text-brand-charcoal cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.beta_interest}
                  onChange={update("beta_interest")}
                  className="h-4 w-4 accent-brand-green"
                  data-testid="early-beta-interest"
                />
                <span>{f.beta_interest}</span>
              </label>
              <label className="md:col-span-2 flex items-start gap-3 text-sm text-brand-charcoal cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={update("consent")}
                  className="mt-0.5 h-4 w-4 accent-brand-green"
                  data-testid="early-consent"
                />
                <span>{f.consent}</span>
              </label>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="mt-6 btn-primary w-full md:w-auto"
              data-testid="early-submit"
            >
              {submitting ? t("common.submitting") : f.submit}
              {!submitting && <ArrowRight className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`} />}
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, placeholder }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-brand-charcoal/70 mb-1.5">
        {label} {required && <span className="text-brand-red">*</span>}
      </span>
      <input type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} data-testid={testid}
        className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-charcoal placeholder:text-brand-charcoal/40 outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors duration-150" />
    </label>
  );
}
function Select({ label, value, onChange, options, placeholder, testid, required }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-brand-charcoal/70 mb-1.5">
        {label} {required && <span className="text-brand-red">*</span>}
      </span>
      <select value={value} onChange={onChange} required={required} data-testid={testid}
        className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-charcoal outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors duration-150">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
